import { CrudMode, Grid, Stack, ValidationErrors, useElementBreakpoints } from "@compass/core";
import {
  LkStudentTabId,
  Permission,
  PermissionAction,
  Student,
  StudentEditResource,
  StudentViewResource,
} from "generated/openApi";
import { BasicInfo } from "./BasicInfo";
import { StudentComments } from "./StudentComments";
import { Diplomas } from "./Diplomas";
import { Enrollments } from "./Enrollments";
import { Fafsas } from "./Fafsas";
import { StudentFiles } from "./StudentFiles";
import { Parents } from "./Parents";
import { Scholarships } from "./Scholarships";
import { Assignment } from "./Assignment";
import { isAuthorized } from "services";
import { PostHighSchools } from "./PostHighSchools";
import { useRouter } from "hooks";
import { studentEmailPageRoute } from "../../StudentEmailPage/StudentEmailPage";
import { Outlet } from "react-router-dom";
import { TextingOptIn } from "./TextingOptIn";

export const StudentProfile = (props: {
  rootPermission: Permission;
  initialLoad: boolean;
  id: number;
  student: Student;
  setStudent: (value: Student) => void;
  crudMode: CrudMode;
  viewResource: StudentViewResource;
  editResource: StudentEditResource;
  validationErrors: ValidationErrors;
  unsavedChanges: boolean;
  save: () => void;
  reroute: (id: number, crudMode: CrudMode) => void;
}) => {
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const studentTabIds = props.viewResource.studentTabs?.map((x) => x.id);
  const { matchTemplatePath } = useRouter();

  return (
    <>
      <Grid ref={breakpointsRef} container spacing="md">
        <Grid item xs={setBreakpoints({ md: 8, xs: 12 })}>
          <Grid container spacing="md">
            <Grid item xs={setBreakpoints({ xs: 12 })}>
              <BasicInfo
                student={props.student}
                setStudent={props.setStudent}
                crudMode={props.crudMode}
                fromStudentApplication={false}
                viewResource={props.viewResource}
                unsavedChanges={props.unsavedChanges}
                save={props.save}
                editResource={props.editResource}
                validationErrors={props.validationErrors}
                reroute={props.reroute}
                hasEditButton
              />
            </Grid>

            {isAuthorized(Permission.StudentEnrollment, PermissionAction.View) &&
              studentTabIds?.includes(LkStudentTabId.Enrollment) && (
                <Grid item xs={setBreakpoints({ xs: 12 })}>
                  <Enrollments initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}

            {isAuthorized(Permission.StudentParent, PermissionAction.View) &&
              studentTabIds?.includes(LkStudentTabId.Parent) && (
                <Grid item xs={setBreakpoints({ md: 6, xs: 12 })}>
                  <Parents initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}

            {isAuthorized(Permission.StudentDiploma, PermissionAction.View) &&
              studentTabIds?.includes(LkStudentTabId.Diploma) && (
                <Grid item xs={setBreakpoints({ md: 6, xs: 12 })}>
                  <Diplomas initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}

            {isAuthorized(Permission.StudentFafsa, PermissionAction.View) &&
              studentTabIds?.includes(LkStudentTabId.Fafsa) && (
                <Grid item xs={setBreakpoints({ md: 6, xs: 12 })}>
                  <Fafsas initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}

            {isAuthorized(Permission.StudentScholarship, PermissionAction.View) &&
              studentTabIds?.includes(LkStudentTabId.Scholarship) && (
                <Grid item xs={setBreakpoints({ md: 6, xs: 12 })}>
                  <Scholarships initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}

            {isAuthorized(Permission.StudentPostHighSchool, PermissionAction.View) &&
              studentTabIds?.includes(LkStudentTabId.PostHs) && (
                <Grid item xs={setBreakpoints({ md: 6, xs: 12 })}>
                  <PostHighSchools initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}

            {isAuthorized(Permission.Texting, PermissionAction.View) &&
              props.student.cellPhone?.length && (
                <Grid item xs={setBreakpoints({ md: 6, xs: 12 })}>
                  <TextingOptIn initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}

            {isAuthorized(Permission.StudentFile, PermissionAction.View) &&
              studentTabIds?.includes(LkStudentTabId.File) && (
                <Grid item xs={setBreakpoints({ xs: 12 })}>
                  <StudentFiles initialLoad={props.initialLoad} studentId={props.id} />
                </Grid>
              )}
          </Grid>
        </Grid>

        <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
          <Stack spacing="md">
            <Assignment
              crudMode={props.crudMode}
              student={props.student}
              setStudent={props.setStudent}
              viewResource={props.viewResource}
              editResource={props.editResource}
              validationErrors={props.validationErrors}
            />

            <StudentComments initialLoad={props.initialLoad} id={props.id} />
          </Stack>
        </Grid>
      </Grid>

      {matchTemplatePath(studentEmailPageRoute.templatePath) && <Outlet />}
    </>
  );
};
